import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import TermsAndConditions from '/@/components/terms-and-conditions';
import settings from '/@/core/settings';
import { type AccessRequestDetail, getAccessRequestApi } from '/@/hooks/access-requests';
import { _ } from '/@/locale';
import { signUp as signUpAction } from '/@/store/session/actions';
import { reverse } from '/@/utils/router';
import { AccessRequestScreen, BrandLogo } from '/@/views/users/request-access/submitted';

/**
 * Identity registration always uses the `bayer` type, with the role in `subtype`. This
 * is the pairing `apps/nodes/identity_mapping.py` maps back to a `UserNode` type, so a
 * wrong subtype here silently lands the user on a GROWER node.
 */
const IDENTITY_TYPE = 'bayer';
const IDENTITY_SUBTYPES = {
    rv: 'sales',
    advisor: 'advisor',
    consultant: 'consultant',
} as const;

const linkSx = { color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' };

const openPrivacyPolicy = () => {
    if (settings.app.privacyPolicy) {
        window.open(settings.app.privacyPolicy, '_blank');
    } else {
        enqueueSnackbar(_('Ha ocurrido un error al cargar la política de privacidad'), { variant: 'error' });
    }
};

/** The model stores a single `name`; Identity wants first and last separately. */
const splitName = (name: string) => {
    const [firstName, ...rest] = name.trim().split(' ');
    return { firstName, lastName: rest.join(' ') };
};

interface FormValues {
    password: string;
    confirmPassword: string;
    acceptedTerms: boolean;
}

const CreatePassword = () => {
    const { token } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const signUp = bindActionCreators(signUpAction, dispatch);

    const [accessRequest, setAccessRequest] = useState<AccessRequestDetail | null>(null);
    const [loadError, setLoadError] = useState('');
    const [error, setError] = useState('');
    const [showTerms, setShowTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // The request-access form sets this when Identity auto-accepted the request, which
    // only changes the heading -- the form itself is identical either way.
    const autoAccepted = Boolean(location.state?.autoAccepted);

    useEffect(() => {
        let cancelled = false;
        getAccessRequestApi(token)
            .then((detail) => {
                if (cancelled) return;
                // Reachable from an acceptance email or a stale link after the account
                // was created. Signing up again can only fail, so send them to login.
                if (detail.userExists) {
                    navigate(reverse('ExistingAccountLogin', { params: { token } }), { replace: true });
                    return;
                }
                setAccessRequest(detail);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError(_('Este link ya no es válido. Volvé a solicitar acceso.'));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [token, navigate]);

    const {
        control,
        handleSubmit,
        formState: { isValid, isSubmitting },
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { password: '', confirmPassword: '', acceptedTerms: false },
    });

    const revealAdornment = (
        <InputAdornment position='end'>
            <IconButton
                aria-label={showPassword ? _('Ocultar contraseña') : _('Mostrar contraseña')}
                onClick={() => setShowPassword((shown) => !shown)}
                edge='end'
            >
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    const onSubmit = async (values: FormValues) => {
        setError('');
        const { firstName, lastName } = splitName(accessRequest.name);
        try {
            await signUp({
                firstName,
                lastName,
                email: accessRequest.email,
                phone: accessRequest.phone,
                password: values.password,
                type: IDENTITY_TYPE,
                subtype: IDENTITY_SUBTYPES[accessRequest.type],
                receiveNotifications: true,
                receiveWhatsappNotifications: false,
            });
            navigate(reverse('Home'));
        } catch (signUpError) {
            setError(signUpError.message);
        }
    };

    const renderBody = () => {
        if (loadError) {
            return (
                <>
                    <Typography variant='subtitle1' sx={{ color: 'grey.700' }}>
                        {loadError}
                    </Typography>
                    <Stack direction='row' justifyContent='center'>
                        <Button
                            variant='contained'
                            onClick={() => navigate(reverse('RequestAccess'))}
                            sx={{ width: { xs: '100%', md: 'auto' } }}
                        >
                            {_('Solicitá acceso')}
                        </Button>
                    </Stack>
                </>
            );
        }

        if (!accessRequest) {
            return (
                <Typography variant='subtitle1' color='primary.mui.inactive'>
                    {_('Cargando...')}
                </Typography>
            );
        }

        return (
            <>
                <Stack spacing={2}>
                    <Typography variant='h5' color='primary' fontWeight={'bold'}>
                        {autoAccepted ? _('¡Ya estás en nuestra base de datos!') : _('Creá tu contraseña')}
                    </Typography>
                    <Typography variant='subtitle1' color='primary.mui.inactive'>
                        {autoAccepted
                            ? _('Confirmamos tu acceso a {{appName}}. Creá tu contraseña para entrar.', {
                                  appName: settings.app.name,
                              })
                            : _('Definí una contraseña para ingresar a {{appName}}', { appName: settings.app.name })}
                    </Typography>
                </Stack>

                <Stack spacing={1}>
                    <Controller
                        name='password'
                        control={control}
                        rules={{ required: true, minLength: 5 }}
                        render={({ field }) => (
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                placeholder={_('Contraseña')}
                                value={field.value}
                                onChange={field.onChange}
                                sx={{ mt: 1 }}
                                InputProps={{ endAdornment: revealAdornment }}
                            />
                        )}
                    />
                    <Controller
                        name='confirmPassword'
                        control={control}
                        rules={{
                            required: true,
                            validate: (value, values) =>
                                value === values.password || _('Las contraseñas no coinciden.'),
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                placeholder={_('Repetir Contraseña')}
                                value={field.value}
                                onChange={field.onChange}
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message}
                                sx={{ mt: 1 }}
                                InputProps={{ endAdornment: revealAdornment }}
                            />
                        )}
                    />
                </Stack>

                <Controller
                    name='acceptedTerms'
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Checkbox
                                    checked={field.value}
                                    onChange={(event) => field.onChange(event.target.checked)}
                                />
                            }
                            label={
                                <Stack direction='row' spacing={0.5} alignItems='center' flexWrap='wrap'>
                                    <Typography variant='body2' color='pprimary.mui.inactive'>
                                        {_('Acepto los')}
                                    </Typography>
                                    <Typography variant='body2' sx={linkSx} onClick={() => setShowTerms(true)}>
                                        {_('términos y condiciones')}
                                    </Typography>
                                    <Typography variant='body2' color='pprimary.mui.inactive'>
                                        {_('y la')}
                                    </Typography>
                                    <Typography variant='body2' sx={linkSx} onClick={openPrivacyPolicy}>
                                        {_('política de privacidad')}
                                    </Typography>
                                </Stack>
                            }
                        />
                    )}
                />

                {error && (
                    <Typography variant='caption' sx={{ color: 'error.main' }}>
                        {error}
                    </Typography>
                )}

                <Stack direction='row' justifyContent='center'>
                    <Button
                        variant='contained'
                        disabled={!isValid || isSubmitting}
                        onClick={handleSubmit(onSubmit)}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        {isSubmitting ? _('Cargando...') : _('Ingresar a {{appName}}', { appName: settings.app.name })}
                    </Button>
                </Stack>
            </>
        );
    };

    return (
        <AccessRequestScreen onBack={() => navigate(reverse('Login'))}>
            <Stack spacing={3}>
                <BrandLogo sx={{ mx: 'auto !important' }} />
                {renderBody()}
                <TermsAndConditions
                    visible={showTerms}
                    onClose={() => setShowTerms(false)}
                    onAccept={() => setShowTerms(false)}
                />
            </Stack>
        </AccessRequestScreen>
    );
};

export default CreatePassword;
